<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <base-input
                col="4"
                type="text"
                :placeholder="$t('PLACEHOLDERS.orderNum')"
                v-model.trim="filterOptions.orderNum"
              />

              <base-input
                col="4"
                type="text"
                :placeholder="$t('PLACEHOLDERS.clientName')"
                v-model.trim="filterOptions.clientName"
              />

              <!-- Start:: Status Input -->
              <base-select-input
                col="4"
                :optionsList="activeStatuses"
                :placeholder="$t('PLACEHOLDERS.orderStatus')"
                v-model="filterOptions.status"
              />

              <base-input
                col="4"
                type="text"
                :placeholder="$t('MESSAGES.Festival name')"
                v-model.trim="filterOptions.festival_name"
              />

              <base-input
                col="4"
                type="text"
                :placeholder="$t('MESSAGES.Festival center')"
                v-model.trim="filterOptions.center_name"
              />
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("MESSAGES.Festivals Orders") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>

        <div
          class="title_route_wrapper"
          v-if="$can('festivals-orders create', 'festivals-orders')"
        >
          <router-link to="/festivals-orders/create">
            {{ $t("MESSAGES.Add new offer") }}
          </router-link>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- Start:: No Data State -->

        <!-- Start:: Item Image -->
        <template v-slot:[`item.id`]="{ item, index }">
          <div class="table_image_wrapper">
            <h6 class="text-danger" v-if="!item.id">
              {{ $t("TABLES.noData") }}
            </h6>
            <p v-else>
              {{
                (paginations.current_page - 1) * paginations.items_per_page +
                index +
                1
              }}
            </p>
          </div>
        </template>
        <!-- End:: Item Image -->

        <!-- Start:: Title -->
        <template v-slot:[`item.name`]="{ item }">
          <p class="text-danger" v-if="!item.name">{{ $t("TABLES.noData") }}</p>
          <p v-else>{{ item.name }}</p>
        </template>
        <template v-slot:[`item._status`]="{ item }">
          <p
            style="
              padding: 8px 5px !important;
              background: var(--main_theme_clr);
              color: var(--white_clr);
              border-color: var(--main_theme_clr);
            "
            class="badge badge-success"
          >
            {{ item._status }}
          </p>
        </template>
        <!-- End:: Title -->
        <!-- Start:: Item Image -->
        <template v-slot:[`item.image`]="{ item }">
          <div class="table_image_wrapper">
            <h6 class="text-danger" v-if="!item.image">
              {{ $t("TABLES.noData") }}
            </h6>

            <button class="my-1" @click="showImageModal(item)" v-else>
              <video
                v-if="item.image.endsWith('mp4')"
                :src="item.image"
                width="150"
                height="100"
                controls
                muted
              ></video>
              <img
                v-else
                class="rounded"
                :src="item.image"
                width="60"
                height="60"
              />
            </button>
          </div>
        </template>
        <!-- End:: Item Image -->

        <!-- Start:: Activation -->
        <template v-slot:[`item.is_active`]="{ item }">
          <div
            class="activation"
            dir="ltr"
            style="z-index: 1"
            v-if="$can('offers activate', 'offers')"
          >
            <v-switch
              class="mt-2"
              color="success"
              v-model="item.is_active"
              hide-details
              @change="changeActivationStatus(item)"
            ></v-switch>
          </div>

          <template v-else>
            <span class="text-success text-h5" v-if="item.is_active">
              <i class="far fa-check"></i>
            </span>
            <span class="text-danger text-h5" v-else>
              <i class="far fa-times"></i>
            </span>
          </template>
        </template>
        <!-- End:: Activation -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <a-tooltip
              placement="bottom"
              v-if="$can('festivalorders show', 'festivalorders')"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button class="btn_show" @click="showItem(item)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>

            <a-tooltip placement="bottom" v-if="item.status !== 'canceled'">
              <template slot="title">
                <span>{{ $t("PLACEHOLDERS.pdf") }}</span>
              </template>
              <button class="btn_show" @click="downloadPdf(item)">
                <i class="fas fa-file-pdf"></i>
              </button>
            </a-tooltip>

            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("MESSAGES.Qr") }}</span>
              </template>
              <button class="btn_show" @click="openQrPage(item)">
                <i class="fas fa-qrcode"></i>
              </button>
            </a-tooltip>

            <a-tooltip placement="bottom" v-if="canMarkAsUsed(item)">
              <template slot="title">
                <span>{{ $t("MESSAGES.markTicketAsUsed") }}</span>
              </template>
              <span>
                <button
                  class="btn_show"
                  :disabled="isWaitingRequest"
                  @click="selectMarkAsUsedItem(item)"
                >
                  <i class="fas fa-check-circle"></i>
                </button>
              </span>
            </a-tooltip>
          </div>
        </template>
        <!-- End:: Actions -->

        <!-- ======================== Start:: Dialogs ======================== -->
        <template v-slot:top>
          <!-- Start:: Image Modal -->
          <image-modal
            v-if="dialogImage"
            :type="selectedItemType"
            :modalIsOpen="dialogImage"
            :modalImage="selectedItemImage"
            @toggleModal="dialogImage = !dialogImage"
          />
          <!-- End:: Image Modal -->

          <!-- Start:: Description Modal -->
          <description-modal
            v-if="dialogDescription"
            :modalIsOpen="dialogDescription"
            :modalDesc="selectedDescriptionTextToShow"
            @toggleModal="dialogDescription = !dialogDescription"
          />
          <!-- End:: Description Modal -->

          <!-- Start:: Delete Modal -->
          <v-dialog v-model="dialogDelete">
            <v-card>
              <v-card-title class="text-h5 justify-center" v-if="itemToDelete">
                {{
                  $t("TITLES.DeleteConfirmingMessage", {
                    name: itemToDelete.name,
                  })
                }}
              </v-card-title>
              <v-card-actions>
                <v-btn class="modal_confirm_btn" @click="confirmDeleteItem">{{
                  $t("BUTTONS.ok")
                }}</v-btn>

                <v-btn class="modal_cancel_btn" @click="dialogDelete = false">{{
                  $t("BUTTONS.cancel")
                }}</v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <!-- End:: Delete Modal -->

          <!-- Start:: Mark As Used Modal -->
          <v-dialog v-model="dialogMarkAsUsed" max-width="480">
            <v-card>
              <v-card-title
                class="text-h6 justify-center"
                v-if="itemToMarkAsUsed"
              >
                {{ $t("MESSAGES.markTicketAsUsedConfirm") }}
              </v-card-title>
              <v-card-subtitle class="text-center pb-0" v-if="itemToMarkAsUsed">
                {{ itemToMarkAsUsed.serial_number }}
              </v-card-subtitle>
              <v-card-actions>
                <v-btn
                  class="modal_confirm_btn"
                  :disabled="isWaitingRequest"
                  @click="confirmMarkAsUsed"
                >
                  {{ $t("BUTTONS.ok") }}
                </v-btn>

                <v-btn
                  class="modal_cancel_btn"
                  :disabled="isWaitingRequest"
                  @click="dialogMarkAsUsed = false"
                >
                  {{ $t("BUTTONS.cancel") }}
                </v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <!-- End:: Mark As Used Modal -->
        </template>
        <!-- ======================== End:: Dialogs ======================== -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";

export default {
  name: "AllFestivalsOrders",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
      getAuthenticatedUserData: "AuthenticationModule/getAuthenticatedUserData",
    }),

    activeStatuses() {
      return [
        {
          id: 1,
          name: this.$t("PLACEHOLDERS.new"),
          value: "new",
        },
        {
          id: 2,
          name: this.$t("MESSAGES.used"),
          value: "used",
        },
        {
          id: 3,
          name: this.$t("MESSAGES.expired"),
          value: "expired",
        },
      ];
    },
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      productCategories: [],
      providers: [],
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        orderNum: null,
        clientName: null,
        status: null,
        festival_name: null,
        center_name: null,
      },
      from_date: null,
      to_date: null,
      // End:: Filter Data

      // Start:: Table Data
      searchValue: "",
      tableHeaders: [
        {
          text: this.$t("TABLES.ImagesSpaces.serialNumber"),
          value: "id",
          align: "center",
          width: "80",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.orderNum"),
          value: "serial_number",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.clientName"),
          value: "user.name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("MESSAGES.Festival name"),
          value: "festival_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("MESSAGES.Festival center"),
          value: "center_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("SIDENAV.OrderData.clientPhone"),
          value: "user.mobile",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.orderStatus"),
          value: "_status",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.created_at"),
          value: "created_at",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.Clients.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Dialogs Control Data
      dialogImage: false,
      selectedItemImage: null,
      dialogDescription: false,
      selectedDescriptionTextToShow: "",
      dialogDelete: false,
      itemToDelete: null,
      dialogMarkAsUsed: false,
      itemToMarkAsUsed: null,
      selectedItemType: null,
      // End:: Dialogs Control Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 6,
      },
      // End:: Pagination Data
    };
  },

  watch: {
    // Start:: Page Query Param Watcher To Get Page Data Based On It's Change
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
    // End:: Page Query Param Watcher To Get Page Data Based On It's Change
  },

  methods: {
    // Start:: Handel Filter
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/festivals-orders/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    async resetFilter() {
      this.filterOptions.clientName = null;
      this.filterOptions.orderNum = null;
      this.filterOptions.status = null;
      this.filterOptions.festival_name = null;
      this.filterOptions.center_name = null;
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/festivals-orders/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    // End:: Handel Filter

    // Start:: Set Table Rows
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      // Scroll To Screen's Top After Get Products
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    },
    async setTableRows() {
      this.loading = true;
      try {
        let params = {
          page: this.paginations.current_page,
          serial_number: this.filterOptions.orderNum,
          user_name: this.filterOptions.clientName,
          status: this.filterOptions.status?.value,
          festival_name: this.filterOptions.festival_name,
          center_name: this.filterOptions.center_name,
        };
        if (this.getAuthenticatedUserData?.type !== "admin") {
          params.festival_provider_id = this.getAuthenticatedUserData?.id;
        }
        let res = await this.$axios({
          method: "GET",
          url: "festival-orders",
          params: params,
        });
        this.loading = false;
        this.tableRows = res.data.data.data;
        this.paginations.last_page = res.data.data.meta.last_page;
        this.paginations.items_per_page = res.data.data.meta.per_page;
      } catch (error) {
        this.loading = false;
        console.log(error.response.data.message);
      }
    },
    async getProductCategories() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "providers?limit=0&page=0",
        });
        this.providers = res.data.data.data;
      } catch (error) {
        this.loading = false;
        console.log(error.response.data.message);
      }
    },
    // End:: Set Table Rows

    showDescriptionModal(description) {
      this.dialogDescription = true;
      this.selectedDescriptionTextToShow = description;
    },
    // End:: Control Modals

    // Start:: Change Activation Status
    async changeActivationStatus(item) {
      try {
        await this.$axios({
          method: "POST",
          url: `festivals-orders/status/${item.id}`,
        });
        this.$message.success(this.$t("MESSAGES.changeActivation"));
      } catch (error) {
        this.setTableRows();
        this.$message.error(error.response.data.message);
      }
    },
    // End:: Change Activation Status

    // ==================== Start:: Crud ====================
    // ===== Start:: End
    editItem(item) {
      this.$router.push({ path: `/festivals-orders/edit/${item.id}` });
    },
    downloadPdf(item) {
      window.open(item.festival_invoice, "_blank");
    },
    showItem(item) {
      this.$router.push({ path: `/festivals-orders/show/${item.id}` });
    },
    openQrPage(item) {
      this.$router.push({ path: `/festivals-orders/qr/${item.id}` });
    },

    canMarkAsUsed(item) {
      if (String(item?.status).toLowerCase() !== "new") return false;
      if (!item?.booking_date) return false;

      const bookingDay = moment(
        String(item.booking_date).trim().slice(0, 10),
        "YYYY-MM-DD",
        true
      );
      if (!bookingDay.isValid()) return false;

      return moment().isSameOrAfter(bookingDay, "day");
    },

    selectMarkAsUsedItem(item) {
      this.dialogMarkAsUsed = true;
      this.itemToMarkAsUsed = item;
    },

    async confirmMarkAsUsed() {
      if (!this.itemToMarkAsUsed) return;

      this.isWaitingRequest = true;
      try {
        await this.$axios({
          method: "POST",
          url: `festival-orders/${this.itemToMarkAsUsed.id}`,
          data: {
            status: "used",
            _method: "PUT",
          },
        });
        this.dialogMarkAsUsed = false;
        this.itemToMarkAsUsed = null;
        this.$message.success(this.$t("MESSAGES.orderMarkedAsUsed"));
        await this.setTableRows();
      } catch (error) {
        this.$message.error(error.response?.data?.message);
      } finally {
        this.isWaitingRequest = false;
      }
    },
    // ===== End:: End

    showImageModal(item) {
      this.dialogImage = true;
      this.selectedItemImage = item.image;
      this.selectedItemType = item.image.endsWith("mp4") ? "video" : "image";
    },

    // ===== Start:: Delete
    selectDeleteItem(item) {
      this.dialogDelete = true;
      this.itemToDelete = item;
    },

    async confirmDeleteItem() {
      try {
        await this.$axios({
          method: "DELETE",
          url: `festivals-orders/${this.itemToDelete.id}`,
        });
        this.dialogDelete = false;
        this.tableRows = this.tableRows.filter((item) => {
          return item.id != this.itemToDelete.id;
        });
        this.setTableRows();
        this.$message.success(this.$t("MESSAGES.deletedSuccessfully"));
      } catch (error) {
        this.dialogDelete = false;
        this.$message.error(error.response.data.message);
      }
    },
    // ===== End:: Delete

    // ==================== End:: Crud ====================
  },

  created() {
    // Start:: Fire Methods
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.getProductCategories();
    this.setTableRows();

    // End:: Fire Methods
  },
};
</script>

<style lang="scss" scoped>
.disable_parent {
  cursor: no-drop !important;
}

.disable_child {
  opacity: 0.5;
  pointer-events: none !important;
}
</style>
